import * as React from 'react'

import { TabsList } from '@ui/components/ui/tabs'
import { cn } from '@ui/lib/utils'

export type ResponsiveTabsListProps = React.ComponentProps<typeof TabsList>

type Edges = { left: boolean; right: boolean }

export function ResponsiveTabsList({ className, ...props }: ResponsiveTabsListProps) {
  const viewportRef = React.useRef<HTMLDivElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)
  const [edges, setEdges] = React.useState<Edges>({ left: false, right: false })

  const updateEdges = React.useCallback(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    const next = {
      left: viewport.scrollLeft > 1,
      right: viewport.scrollLeft + viewport.clientWidth < viewport.scrollWidth - 1,
    }
    setEdges((current) =>
      current.left === next.left && current.right === next.right ? current : next,
    )
  }, [])

  const ensureActiveVisible = React.useCallback(() => {
    const viewport = viewportRef.current
    const list = listRef.current
    const active = list?.querySelector<HTMLElement>('[data-state="active"]')
    if (!viewport || !active) return

    const viewportRect = viewport.getBoundingClientRect()
    const activeRect = active.getBoundingClientRect()
    if (activeRect.left < viewportRect.left || activeRect.right > viewportRect.right) {
      active.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
    }
  }, [])

  React.useEffect(() => {
    const viewport = viewportRef.current
    const list = listRef.current
    if (!viewport || !list) return

    const refresh = () => {
      ensureActiveVisible()
      updateEdges()
    }

    refresh()

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(refresh)
    resizeObserver?.observe(viewport)
    resizeObserver?.observe(list)

    const mutationObserver =
      typeof MutationObserver === 'undefined' ? null : new MutationObserver(refresh)
    mutationObserver?.observe(list, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-state'],
    })

    return () => {
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
    }
  }, [ensureActiveVisible, updateEdges])

  return (
    <div className="relative min-w-0">
      <div
        ref={viewportRef}
        data-slot="responsive-tabs-viewport"
        className="max-w-full overflow-x-auto overflow-y-hidden pb-1"
        onScroll={updateEdges}
      >
        <TabsList ref={listRef} className={cn('max-w-none', className)} {...props} />
      </div>
      {edges.left ? (
        <div
          aria-hidden="true"
          data-edge="left"
          className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-background to-transparent"
        />
      ) : null}
      {edges.right ? (
        <div
          aria-hidden="true"
          data-edge="right"
          className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-background to-transparent"
        />
      ) : null}
    </div>
  )
}
